'use client';

import { useCallback, useMemo, useRef } from 'react';
import { useI18n } from '~/features/i18n';
import { parseAsString, useQueryState } from 'nuqs';
import type { UIMessage } from 'ai';
import { DEFAULT_AI_MODEL_ID } from '@funkey/ai-tools/schemas';
import { generateThreadId, useActivityAiChatStore } from './activity-ai-chat-store';
import { setInFlightThreadId as setSharedInFlightThreadId, useInFlightThreadId } from './activity-ai-chat-runtime-store';
import { streamThreadTitle } from './stream-thread-title';
import { clearDraftChat, promoteDraftChat, sendMessageToThread, useActivityAiChat } from './useActivityAiChat';
import { AI_SEARCH_EVENTS, useAiSearchAnalytics } from './ai-search-analytics';
import { buildReplyRequestBody } from './quote-reply';
import { useTrackAssistantTurns, useTrackChatErrors } from './useTrackChatTurns';
import { useActivitySearchCtx } from './client/context';
import { getChatLocale, setChatLocale } from './chat-locale-store';

/**
 * Drives the active chat thread via a `?chat=<id>` URL param. The thread list
 * itself lives in the zustand store (persisted to localStorage); the URL only
 * tracks which thread is currently open.
 *
 * Empty string => "all chats" landing (suggestions + history).
 *
 * Messages live in the in-memory `Chat` instance from `useActivityAiChat`. This
 * hook bridges the URL param + the `useChat` helpers so existing call sites can
 * keep using `aiChat.messages` (mapped to `{ id, role, text }`) and
 * `aiChat.sendMessage(text)`.
 */
export function useActivityAiActiveChat() {
  const [chatId, setChatId] = useQueryState('chat', parseAsString.withDefault('').withOptions({
    shallow: true,
    history: 'replace'
  }));
  const upsertThread = useActivityAiChatStore(s => s.upsertThread);
  const setThreadTitle = useActivityAiChatStore(s_0 => s_0.setThreadTitle);
  const draftModelId = useActivityAiChatStore(s_1 => s_1.draftModelId);
  const thread = useActivityAiChatStore(s_2 => chatId ? s_2.threads[chatId] : undefined);
  const activeModelId = draftModelId ?? DEFAULT_AI_MODEL_ID;
  const {
    messages,
    sendMessage,
    status,
    stop,
    error,
    regenerate,
    setMessages,
    isHydrating,
    reloadMessages
  } = useActivityAiChat(chatId || null);

  // Site locale (nl-BE, fr-BE, …) → coarse chat locale ('nl' | 'fr' | 'en').
  // Sent in every request body so the route's refusal text + the model's
  // reply language match the site, not the browser's Accept-Language.
  const i18n = useI18n();
  const chatLocale: 'nl' | 'fr' | 'en' = i18n.language === 'fr' ? 'fr' : i18n.language === 'en' ? 'en' : 'nl';
  // Mirror into the module store so the transport can pin the reply language on
  // every send path — including regenerate/edit, which carry no caller `locale`.
  // Idempotent render write (guarded), so no effect is needed to sync it.
  if (getChatLocale() !== chatLocale) setChatLocale(chatLocale);
  const analytics = useAiSearchAnalytics();
  // Submit timestamp keyed by session/thread id so latency can be computed
  // when the assistant turn lands, even across re-renders.
  const submitTimestampsRef = useRef<Map<string, number>>(new Map());
  // Model id used at submit time (sticky across the assistant turn that follows).
  const submitModelIdRef = useRef<Map<string, string>>(new Map());
  // Prompt length captured at submit time, kept around so `no_results`
  // can report it without ever holding the prompt text itself.
  const submitPromptLengthRef = useRef<Map<string, number>>(new Map());
  // In-flight thread id captured at submit time. The first turn of a new chat
  // runs on the draft `Chat` while the URL `?chat=` is still empty (it only
  // flips after `sendMessage` resolves), so the effect below has no `chatId`
  // to key on. This ref bridges that gap so first-turn analytics fire.
  const inFlightThreadIdRef = useRef<string | null>(null);
  // Shared store mirror of `inFlightThreadIdRef` so consumers in OTHER hook
  // instances (the grid in particular) re-render the moment a new thread id
  // is assigned — before the URL `?chat=` has caught up. Local state was not
  // enough because each `useActivityAiActiveChat` call had its own copy.
  const inFlightThreadId = useInFlightThreadId();
  const setInFlightThreadId = setSharedInFlightThreadId;

  // Returning to the landing must reset the applied filters too: a previous
  // chat's filters (region, cleanliness, …) live in the URL and would otherwise
  // leak into the next chat — both as the grid constraint and as `activeFilters`
  // on the next turn. We only need `clear`, not the live filter value.
  const clearFilters = useActivitySearchCtx().useFilters().clear;

  /**
   * Read + clear the submit-time context for `threadId`. Centralised so the
   * effect can ship `results_shown` and `no_results` without duplicating the
   * three `Map.get` + `Map.delete` pairs (and so `prompt_length` is reused by
   * both branches without becoming a free-floating single-use local).
   */
  function consumeSubmitContext(threadId: string) {
    const submitTs = submitTimestampsRef.current.get(threadId);
    submitTimestampsRef.current.delete(threadId);
    const modelExtraction = submitModelIdRef.current.get(threadId) ?? activeModelId;
    submitModelIdRef.current.delete(threadId);
    const promptLength = submitPromptLengthRef.current.get(threadId) ?? 0;
    submitPromptLengthRef.current.delete(threadId);
    return {
      latencyMs: submitTs ? Date.now() - submitTs : 0,
      modelExtraction,
      promptLength
    };
  }

  /** Map raw `UIMessage` -> the legacy `{ id, role, text }` shape callers depend on. */
  const legacyMessages = useMemo(() => messages.map(m => ({
    id: m.id,
    role: m.role,
    text: collectText(m)
  })), [messages]);
  const sendMessageString = useCallback((text: string, metadata?: Record<string, unknown>, activeFilters?: Record<string, unknown>) => {
    const trimmed = text.trim();
    if (trimmed.length === 0) return chatId || '';
    const isNewThread = chatId.length === 0;

    // First message while `?chat` is unset must run entirely on the **draft**
    // `Chat` instance, then we copy the transcript onto the new thread id.
    // Promoting *before* `sendMessage` left the new thread empty and stranded the
    // user turn on the draft — the URL could update while the UI still read the
    // wrong `Chat` (classic "new prompt from floating composer didn't stick").
    if (isNewThread) {
      const activeThreadId = generateThreadId();
      inFlightThreadIdRef.current = activeThreadId;
      setInFlightThreadId(activeThreadId);
      submitTimestampsRef.current.set(activeThreadId, Date.now());
      submitModelIdRef.current.set(activeThreadId, activeModelId);
      submitPromptLengthRef.current.set(activeThreadId, trimmed.length);
      analytics[AI_SEARCH_EVENTS.PROMPT_SUBMITTED]({
        is_followup: false,
        prompt_length: trimmed.length,
        session_id: activeThreadId
      });
      upsertThread(activeThreadId, '');
      void streamThreadTitle(activeThreadId, trimmed, chunk => setThreadTitle(activeThreadId, chunk));
      void (async () => {
        try {
          await sendMessage({
            text: trimmed,
            metadata
          }, {
            body: {
              model: activeModelId,
              chatId: activeThreadId,
              locale: chatLocale,
              siteLocale: i18n.locale,
              activeFilters
            }
          });
        } catch {
          // Stop / network error — still persist whatever made it onto draft.
        } finally {
          promoteDraftChat(activeThreadId);
          void setChatId(activeThreadId);
        }
      })();
      return activeThreadId;
    }
    const activeThreadId_0 = chatId;
    inFlightThreadIdRef.current = activeThreadId_0;
    setInFlightThreadId(activeThreadId_0);
    submitTimestampsRef.current.set(activeThreadId_0, Date.now());
    submitModelIdRef.current.set(activeThreadId_0, activeModelId);
    submitPromptLengthRef.current.set(activeThreadId_0, trimmed.length);
    analytics[AI_SEARCH_EVENTS.PROMPT_SUBMITTED]({
      is_followup: messages.length > 0,
      prompt_length: trimmed.length,
      session_id: activeThreadId_0
    });
    if (!thread || thread.title.length === 0) {
      upsertThread(activeThreadId_0, '');
      void streamThreadTitle(activeThreadId_0, trimmed, chunk_0 => setThreadTitle(activeThreadId_0, chunk_0));
    }
    void sendMessage({
      text: trimmed,
      metadata
    }, {
      body: {
        model: activeModelId,
        chatId: activeThreadId_0,
        locale: chatLocale,
        siteLocale: i18n.locale,
        activeFilters
      }
    });
    return activeThreadId_0;
  }, [activeModelId, analytics, chatId, chatLocale, draftModelId, messages.length, sendMessage, setChatId, setThreadTitle, thread, upsertThread]);

  /**
   * Force-create a brand new thread and send `text` as the first message, even
   * if `?chat=` is already pointing at a different thread. Used by the hero
   * composer so submitting "Zoek teambuilding" while an old chat is open
   * opens a fresh conversation instead of appending to it.
   */
  const startNewChat = useCallback((text_0: string) => {
    const trimmed_0 = text_0.trim();
    if (trimmed_0.length === 0) return '';
    const newThreadId = generateThreadId();
    const nextModelId = draftModelId ?? DEFAULT_AI_MODEL_ID;
    inFlightThreadIdRef.current = newThreadId;
    setInFlightThreadId(newThreadId);
    submitTimestampsRef.current.set(newThreadId, Date.now());
    submitModelIdRef.current.set(newThreadId, nextModelId);
    submitPromptLengthRef.current.set(newThreadId, trimmed_0.length);
    analytics[AI_SEARCH_EVENTS.PROMPT_SUBMITTED]({
      is_followup: false,
      prompt_length: trimmed_0.length,
      session_id: newThreadId
    });
    upsertThread(newThreadId, '');
    void streamThreadTitle(newThreadId, trimmed_0, chunk_1 => setThreadTitle(newThreadId, chunk_1));
    // Mirror `sendMessageString`'s draft-then-promote pattern: drop the
    // URL `?chat=` so every consumer rebinds onto the draft `Chat`
    // instance, stream the first turn there, then promote the draft
    // onto `newThreadId` and flip the URL. The panel reading the draft
    // sees messages arrive live, and the bridge mounted inside it
    // writes the activity ids under `newThreadId` (via the
    // `inFlightThreadId` fallback) BEFORE the grid query refires —
    // closing the race that previously left the grid blank until
    // refresh. Writing straight to a fresh thread (the previous
    // behaviour) skipped the draft entirely, so the panel kept
    // reading the old thread while the grid queried an empty
    // sessionStorage entry for `newThreadId`.
    clearDraftChat();
    void setChatId('');
    void (async () => {
      try {
        await sendMessageToThread('__draft__', trimmed_0, {
          body: {
            model: nextModelId,
            chatId: newThreadId,
            locale: chatLocale,
            siteLocale: i18n.locale
          }
        });
      } catch {
        // Stop / network error — still persist whatever made it onto draft.
      } finally {
        promoteDraftChat(newThreadId);
        void setChatId(newThreadId);
      }
    })();
    return newThreadId;
  }, [analytics, chatLocale, draftModelId, setChatId, setThreadTitle, upsertThread]);
  const goToAllChats = useCallback(() => {
    // Always reset the scratch buffer when returning to the landing surface so a
    // half-typed draft never masquerades as "no thread" while the header still
    // thinks a conversation is active (`messages` came from the wrong `Chat`).
    clearDraftChat();
    // Clear the in-flight thread id too — otherwise the grid (which falls back
    // to `inFlightThreadId` when `chatId` is empty) keeps reading the previous
    // thread's filter IDs from sessionStorage instead of resetting to "no filter".
    inFlightThreadIdRef.current = null;
    setInFlightThreadId(null);
    void setChatId('');
    // Reset the URL filter params so the next chat starts unfiltered instead
    // of inheriting the previous conversation's region/cleanliness/etc.
    void clearFilters();
  }, [setChatId, setInFlightThreadId, clearFilters]);
  const openThread = useCallback((id: string) => {
    void setChatId(id);
  }, [setChatId]);

  /**
   * Truncate the conversation back to (but not including) the message at
   * `messageId`, then resend `text` as a fresh user turn. Used by the inline
   * "edit a previous prompt" UX so the user can branch from any earlier
   * message when the model went off track.
   */
  /**
   * Send `text` as a NEW user turn that quotes an earlier message. Unlike
   * `editAndResend`, this preserves the full transcript — the quoted message
   * is attached as `quotedMessage` in the request body so the server can
   * prepend a synthetic system message that re-anchors the model.
   */
  const replyToMessage = useCallback((messageId: string, text_1: string) => {
    const trimmed_1 = text_1.trim();
    if (trimmed_1.length === 0) return;
    const quoted = messages.find(m_0 => m_0.id === messageId);
    if (!quoted) return;
    void sendMessage({
      text: trimmed_1
    }, {
      body: buildReplyRequestBody({
        model: activeModelId,
        chatId,
        quotedMessage: quoted,
        locale: chatLocale,
        siteLocale: i18n.locale
      })
    });
  }, [activeModelId, chatId, chatLocale, messages, sendMessage]);
  const editAndResend = useCallback((messageId_0: string, text_2: string) => {
    const trimmed_2 = text_2.trim();
    if (trimmed_2.length === 0) return;
    const idx = messages.findIndex(m_1 => m_1.id === messageId_0);
    if (idx === -1) return;
    // Drop the target message AND every message that came after it —
    // those replies were responses to the old prompt and are now stale.
    const truncated = messages.slice(0, idx);
    setMessages(truncated);
    void sendMessage({
      text: trimmed_2
    }, {
      body: {
        model: activeModelId,
        chatId,
        locale: chatLocale,
        siteLocale: i18n.locale
      }
    });
  }, [activeModelId, chatId, chatLocale, messages, sendMessage, setMessages]);

  // Fire `results_shown` / `no_results` exactly once per assistant turn, and
  // capture `REFUSAL` once per new error reference. Both effects live in
  // hooks (the guide's accepted escape hatch) so this hook doesn't ship raw
  // `useEffect`s carrying ref-as-dedupe gates inline.
  const getAnalyticsSessionId = () => chatId.length > 0 ? chatId : inFlightThreadIdRef.current;
  useTrackAssistantTurns({
    status,
    messages,
    getSessionId: getAnalyticsSessionId,
    analytics,
    consumeSubmitContext,
    onDispatched: () => {
      inFlightThreadIdRef.current = null;
      setInFlightThreadId(null);
    }
  });
  useTrackChatErrors({
    error,
    getSessionId: getAnalyticsSessionId,
    analytics
  });
  return {
    // Prefer the URL chat id when present (stable across reloads). Fall
    // back to the in-flight thread id so consumers can scope per-thread
    // state (e.g. the AI results bridge writing to sessionStorage) before
    // the URL `?chat=` flips — which only happens after the first
    // `sendMessage` resolves.
    chatId: chatId || inFlightThreadId || '',
    thread,
    /** Raw AI SDK UIMessage[] — use this in renderers that need `parts`. */
    uiMessages: messages,
    /** Legacy `{ id, role, text }[]` shape kept for non-renderer call sites. */
    messages: legacyMessages,
    threadTitle: thread?.title ?? '',
    /** True while a freshly-opened thread's transcript is still loading from Postgres. */
    isHydrating,
    /** Re-fetch the open thread's transcript (manual retry after an empty/failed load). */
    reloadMessages,
    sendMessage: sendMessageString,
    status,
    stop,
    error,
    regenerate: (options?: {
      messageId?: string;
    }) => regenerate({
      ...(options?.messageId !== undefined ? {
        messageId: options.messageId
      } : {}),
      // `regenerate` skips our explicit per-call body, so backfill
      // locale (and the route-required chatId) here. Without this the
      // route falls back to Accept-Language for the refusal text.
      body: {
        chatId,
        locale: chatLocale,
        siteLocale: i18n.locale
      }
    }),
    goToAllChats,
    startNewChat,
    openThread,
    editAndResend,
    replyToMessage
  };
}
function collectText(message: UIMessage): string {
  return message.parts.filter(p => p.type === 'text').map(p => p.type === 'text' ? p.text : '').join('');
}